import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { Project } from 'app/models';
import { ProjectService, UserService } from 'app/services';
import { AccountService, TransferAccount } from 'app/services/account/account.service';

interface TransferUser {
  userId: number;
  name: string;
  userName: string;
  label?: string;
}

@Component({
  selector: 'app-transfer-project',
  templateUrl: './transfer-project.component.html',
  styles: [
    `
      :host ::ng-deep .save .p-button {
        background: #465675 !important;
        border-color: transparent !important;
        box-shadow: none;
      }
    `
  ]
})
export class TransferProjectComponent implements OnDestroy {
  @Input() project: Project;
  @Output() closed = new EventEmitter<void>();
  @Output() transferred = new EventEmitter<Project>();
  public visible = true;
  public account: TransferAccount | string = null;
  public user: TransferUser = null;
  public accounts: TransferAccount[] = null;
  public users: TransferUser[] = [];
  public loadingUsers = false;
  public saving = false;
  public error = '';
  private accountRequest = new Subscription();
  private userRequest = new Subscription();
  private transferRequest = new Subscription();

  constructor(
    private accountService: AccountService,
    private userService: UserService,
    private projectService: ProjectService
  ) {}

  public get selectedAccount(): TransferAccount {
    return this.account && typeof this.account !== 'string' ? this.account : null;
  }

  public get canTransfer(): boolean {
    return !!this.project && !!this.selectedAccount &&
      !!this.user &&
      this.users.some(item => item.userId === this.user.userId) &&
      !this.loadingUsers && !this.saving;
  }

  public accountChanged(value: TransferAccount | string): void {
    if (value && typeof value !== 'string' && this.selectedAccount &&
        value.id === this.selectedAccount.id && !this.error) {
      return;
    }
    this.account = value;
    this.accountRequest.unsubscribe();
    this.accounts = null;
    this.userRequest.unsubscribe();
    this.user = null;
    this.users = [];
    this.loadingUsers = false;
    this.error = '';
    if (!this.selectedAccount) {
      return;
    }
    this.loadingUsers = true;
    this.userRequest = this.userService.getUsersByAccountId(this.selectedAccount.id).subscribe(
      (users: TransferUser[]) => {
        this.users = users.map(user => ({
          ...user,
          label: user.userName ? `${user.name} (${user.userName})` : user.name
        })).sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }) ||
          (a.userName || '').localeCompare(b.userName || '', undefined, { sensitivity: 'base' })
        );
        this.loadingUsers = false;
      },
      () => {
        this.loadingUsers = false;
        this.error = 'Unable to load users. Please select the account again.';
      }
    );
  }

  public searchAccounts({ query }: { query: string }): void {
    this.accountRequest.unsubscribe();
    // PrimeNG treats [] as a completed search and stops processing later results.
    // Keep suggestions null until the request returns its actual results.
    this.accounts = null;
    this.error = '';
    if (!query.trim()) {
      return;
    }
    this.accountRequest = this.accountService.search(query.trim()).subscribe(
      accounts => this.accounts = accounts,
      () => this.error = 'Unable to search accounts. Please try again.'
    );
  }

  public transfer(): void {
    if (!this.canTransfer) {
      return;
    }
    const account = this.selectedAccount;
    const user = this.user;
    this.saving = true;
    this.error = '';
    this.transferRequest = this.projectService.transfer(this.project.value, account.id, user.userId).subscribe(
      () => {
        this.saving = false;
        this.transferred.emit(new Project({
          ...this.project,
          accountId: account.id,
          accountName: account.name,
          userId: user.userId,
          userName: user.name
        }));
        this.closed.emit();
      },
      () => {
        this.saving = false;
        this.error = 'Unable to transfer project. Please try again.';
      }
    );
  }

  ngOnDestroy(): void {
    this.accountRequest.unsubscribe();
    this.userRequest.unsubscribe();
    this.transferRequest.unsubscribe();
  }
}
